import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import { useSelector, useDispatch } from "react-redux";
import { get_analytics_data } from "../../reduxData/user/userAction";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, ShieldAlert, Split, Undo2 } from "lucide-react";


const Analytics = () => {
    const { t } = useTranslation();
    const { analyticsData } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [timeRange, setTimeRange] = useState('7d');

    const statsData = [
        {
            label: t("analytics.totalGmv"),
            value: analyticsData ? Number(analyticsData?.totalSale?.total_sale) : 0,
            color: "#e07b3042",
            icon: <i className={`bi bi-graph-up-arrow card-icon`}></i>,
            prefix: "€",
        },
        {
            label: t("analytics.commissionRevenue"),
            value: analyticsData ? Number(analyticsData?.platformCommission?.total_platform_commission) : 0,
            color: "#e07b3042",
            icon: <i className={`bi bi-currency-euro card-icon`}></i>,
            prefix: "€",
        },
        {
            label: t("analytics.activeBoutiques"),
            value: analyticsData ? Number(analyticsData?.totalActiveSellers?.total_active_sellers) : 0,
            color: "#e07b3042",
            icon: <i className={`bi bi-shop card-icon`}></i>,
            link: '/users',
        },
        {
            label: t("analytics.totalOrders"),
            value: analyticsData ? Number(analyticsData?.totalOrders?.total_orders) : 0,
            color: "#e07b3042",
            icon: <i className={`bi bi-cart-check card-icon`}></i>,
            link: '/orders',
        },
        {
            label: t("analytics.pendingProducts"),
            value: analyticsData ? Number(analyticsData?.pendingProducts?.pending_products) : 0,
            color: "#e07b3042",
            icon: <Box className="card-icon" />,
            prefix: "",
        },
        {
            label: t("analytics.openReturns"),
            value: analyticsData ? Number(analyticsData?.openReturns?.open_returns) : 0,
            color: "#e07b3042",
            icon: <Undo2 className="card-icon" />,
            prefix: "",
        },
        {
            label: t("analytics.openDisputes"),
            value: analyticsData ? Number(analyticsData?.openDisputes?.open_disputes) : 0,
            color: "#e07b3042",
            icon: <Split className="card-icon" />,
            prefix: "",
        },
        {
            label: t("analytics.blockedPayouts"),
            value: analyticsData ? Number(analyticsData?.blockedPayouts?.blocked_payouts) : 0,
            color: "#e07b3042",
            icon: <ShieldAlert className="card-icon" />,
            prefix: "€",
        },
        {
            label: t("analytics.userStatistics"),
            isMulti: true,
            link: '/users',
            stats: [
                {
                    label: t("analytics.totalSellers"),
                    value: analyticsData ? Number(analyticsData?.totalSellers?.total_sellers) : 0
                },
                {
                    label: t("analytics.pendingSellers"),
                    value: analyticsData ? Number(analyticsData?.totalSellers?.total_sellers) : 0
                },
            ],
            color: "#e07b3042",
            icon: <i className={`bi bi-people card-icon`}></i>,
        },
        {
            label: t("analytics.buyerStatistics"),
            isMulti: true,
            link: '/users',
            stats: [
                {
                    label: t("analytics.totalBuyers"),
                    value: analyticsData ? Number(analyticsData?.totalBuyers?.total_buyers) : 0
                },
            ],
            color: "#e07b3042",
            icon: <i className={`bi bi-people card-icon`}></i>,
        },
    ];

    // fetch analytics data 
    const fetchData = async (selectedRange = timeRange) => {
        await get_analytics_data(dispatch, selectedRange);
    };

    // hanlde navigate users screen 
    const handlenavigate = async (item) => {
        if (item?.label == 'Active Boutiques') {
            navigate('/users', { state: { activeSeller: true } })
        }
        else {
            navigate(`${item.link ? item.link : '/'}`)
        }
    };

    useEffect(() => {
        fetchData(timeRange);
    }, [timeRange]);

    return (
        <div className="analytics-wrapper">
            <div className="container">
                <div className="analytics-header">
                    <div>
                        <h1 className="analytics-title">{t("analytics.dashboardTitle")}</h1>
                        <p className="analytics-subtitle">{t("analytics.dashboardSubtitle")}</p>
                    </div>
                    <div className="d-flex justify-content-end">
                        <div className="segmented-control">
                            <button
                                className={`segment ${timeRange === "7d" ? "active" : ""}`}
                                onClick={() => setTimeRange("7d")}
                            > {t("analytics.timeRange7d")} </button>
                            <div className='divideline'></div>
                            <button
                                className={`segment ${timeRange === "30d" ? "active" : ""}`}
                                onClick={() => setTimeRange("30d")}
                            > {t("analytics.timeRange30d")} </button>
                            <div className='divideline'></div>
                            <button
                                className={`segment ${timeRange === "all" ? "active" : ""}`}
                                onClick={() => setTimeRange("all")}
                            > {t("analytics.timeRangeAll")} </button>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    {statsData.map((item, index) => (
                        <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3">
                            <div className={`analytics-card ${item?.link && 'cursor-pointer'}`} style={{ "--card-color": item.color }} onClick={() => { handlenavigate(item) }}
                            >
                                <div className="card-icon-wrapper bg-opacity-10">
                                   {item.icon} <i className={`bi ${item.icon} card-icon`}></i>
                                </div>
                                <p className="card-label">{item.label}</p>
                                {!item.isMulti ? (
                                    <div className="card-value">
                                        {item.prefix && <span className="value-prefix">{item.prefix}</span>}
                                        <h2 className="value-number">
                                            <CountUp
                                                end={item.value}
                                                duration={2.5}
                                                separator=","
                                                useEasing={true}
                                            />
                                        </h2>
                                    </div>
                                ) : (
                                    <div className="multi-stats">
                                        {item.stats.map((stat, idx) => (
                                            <div key={idx} className="stat-row">
                                                <span className="stat-label">{stat.label}</span>
                                                <span className="stat-value">
                                                    <CountUp
                                                        end={stat.value}
                                                        duration={2.5}
                                                        separator=","
                                                        useEasing={true}
                                                    />
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Analytics;


